// Risk Management Service
// Frontend service for risk management operations

import { Risk, CreateRiskRequest, UpdateRiskRequest, AssignRiskRequest, RiskPoolFilters } from '../types/risk';
import api from './api';

export class RiskService {

  // Risk CRUD Operations
  async createRisk(riskData: CreateRiskRequest): Promise<Risk> {
    const response = await api.post('/risks', riskData);
    return response.data.data;
  }

  async getRiskById(riskId: string): Promise<Risk> {
    const response = await api.get(`/risks/${riskId}`);
    return response.data.data;
  }

  async updateRisk(riskId: string, updateData: UpdateRiskRequest): Promise<Risk> {
    const response = await api.put(`/risks/${riskId}`, updateData);
    return response.data.data;
  }

  async deleteRisk(riskId: string): Promise<void> {
    await api.delete(`/risks/${riskId}`);
  }

  // Risk Pool Operations
  async getRiskPool(filters: RiskPoolFilters = {}, page = 1, limit = 20): Promise<{ risks: Risk[]; pagination: any }> {
    const params: any = {
      page: page.toString(),
      limit: limit.toString()
    };
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        if (Array.isArray(value)) {
          if (value.length > 0) params[key] = value.join(',');
        } else {
          params[key] = value.toString();
        }
      }
    });

    const response = await api.get('/risks/pool', { params });
    return response.data.data;
  }

  // Risk Assignment Operations
  async assignRisk(riskId: string, assignmentData: AssignRiskRequest): Promise<void> {
    await api.post(`/risks/${riskId}/assign`, assignmentData);
  }

  async acceptRisk(riskId: string, acceptanceData: { acceptance_comment?: string }): Promise<void> {
    await api.post(`/risks/${riskId}/accept`, acceptanceData);
  }

  async dismissRisk(riskId: string, dismissalData: { dismissal_reason: string; dismissal_comment?: string }): Promise<void> {
    await api.post(`/risks/${riskId}/dismiss`, dismissalData);
  }

  async reassignRisk(riskId: string, reassignmentData: { new_owner_id: string; reassignment_reason: string; reassignment_comment?: string }): Promise<void> {
    await api.post(`/risks/${riskId}/reassign`, reassignmentData);
  }

  // Risk Escalation Operations
  async escalateRisk(riskId: string, escalationData: { escalated_to_id: string; escalation_reason: string; escalation_comment?: string }): Promise<void> {
    await api.post(`/risks/${riskId}/escalate`, escalationData);
  }

  // Risk Comment Operations
  async addRiskComment(riskId: string, commentData: { comment_text: string; comment_type?: string; action_context?: string }): Promise<void> {
    await api.post(`/risks/${riskId}/comments`, commentData);
  }

  async getRiskComments(riskId: string): Promise<any[]> {
    const response = await api.get(`/risks/${riskId}/comments`);
    return response.data.data;
  }

  // Bulk Operations
  async bulkAssignRisks(riskIds: string[], ownerId: string, assignmentReason: string): Promise<void> {
    await api.post('/risks/pool/bulk-assign', {
      risk_ids: riskIds,
      owner_id: ownerId,
      assignment_reason: assignmentReason
    });
  }

  // Risk Analytics
  async getRiskAnalytics(): Promise<any> {
    const response = await api.get('/risks/analytics');
    return response.data.data;
  }

  async getRiskDashboard(): Promise<any> {
    const response = await api.get('/risks/dashboard');
    return response.data.data;
  }

  async getRecentActivity(page: number = 1, limit: number = 15): Promise<any[]> {
    const response = await api.get('/risks/recent-activity', { params: { page, limit } });
    return response.data.data ?? [];
  }

  async getUpcomingDeadlines(page: number = 1, limit: number = 15): Promise<any[]> {
    const response = await api.get('/risks/upcoming-deadlines', { params: { page, limit } });
    return response.data.data ?? [];
  }

  // Risk Categories and Registers
  async getRiskCategories(): Promise<any[]> {
    const response = await api.get('/risks/categories');
    return response.data.data;
  }

  async getRiskRegisters(): Promise<any[]> {
    const response = await api.get('/risks/registers');
    return response.data.data;
  }
}
